import { useRef } from "react";
import { twJoin } from "tailwind-merge";

import type { PollDto } from "@/api/types";
import { Anchor } from "@/components/Anchor/Anchor";
import { Button } from "@/components/Button/Button";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { CountdownLabel } from "@/components/CountdownLabel/CountdownLabel";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { EyeOffIcon } from "@/components/Icons/EyeOffIcon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Label } from "@/components/Label/Label";
import { LinkFormatter } from "@/components/LinkFormatter/LinkFormatter";
import { Pdf } from "@/components/Pdf/Pdf";
import { formatDistance } from "@/helpers/date";
import { twResolve } from "@/helpers/tw-resolve";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useViewportCollision } from "@/modules/messages/helpers";
import { pollMutations } from "@/queries/polls/mutations";
import { useTranslation } from "@/translations";

import { CodeOfConductDeletionModal } from "../CodeOfConductDeletionModal/CodeOfConductDeletionModal";
import { CommunityItem } from "../CommunityItem";
import { CommunityItemGallery } from "../CommunityItemMedia/CommunityItemGallery";
import { CommunityItemVideo } from "../CommunityItemMedia/CommunityItemVideo";
import { CommunityPollResultModal } from "./CommunityPollResultModal";
import { CommunityPollResultSection } from "./CommunityPollResultSection";
import { CommunityPollVoteForm } from "./CommunityPollVoteForm";

interface CommunityPollProps {
  poll: PollDto;
}
export function CommunityPoll({ poll }: CommunityPollProps): React.ReactNode {
  const [isDeleteModalOpened, deleteModalHandler] = useBool(false);
  const [isAppPollModalOpened, appPollModalHandler] = useBool(false);
  const [isResultModalOpened, resultModalHandler] = useBool(false);

  const refContainer = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const hasBeenInViewport = useViewportCollision(refContainer);

  const updatePollReadStatusMutation = pollMutations.useUpdatePollReadStatus();
  const deletePollMutation = pollMutations.useDeletePoll();

  const getRing = () => {
    if (poll.deletedAt) {
      return "greyed-out";
    } else if (poll.isUnread) {
      return "unread";
    }

    return undefined;
  };

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("component.community-post.actions.mark-as-read"),
      },
      icon: <CheckIcon />,
      value: "mark-read",
      type: "button",
      callbackFn: () =>
        updatePollReadStatusMutation.mutate({
          pollId: poll.id,
        }),
      isHidden: !poll.isUnread,
    },
    {
      labels: {
        default: t("component.community-poll.actions.delete"),
      },
      icon: <Trash02Icon />,
      value: "delete",
      type: "button",
      "data-testid": "context-menu-delete-btn",
      callbackFn: deleteModalHandler.toggle,
      isHidden: !poll.canDeleteOwn && !poll.canDeleteOther,
    },
  ];
  const endDate = poll.endsAt ? new Date(poll.endsAt) : undefined;

  return (
    <>
      <CommunityItem
        ring={getRing()}
        user={poll.createdBy}
        group={poll.group}
        actions={menuActions}
        dateSubtitle={poll.createdAt && <FormattedDate format="datetime" date={poll.createdAt} />}
        warningHeaders={
          poll.isHiddenFromAdmins
            ? [
                {
                  icon: <EyeOffIcon />,
                  text: t("component.community-post.hidden-from-property-manager"),
                },
              ]
            : undefined
        }
        labels={[
          poll.projectConnection ? (
            <Label theme="blue" icon={<MarkerPin01Icon />}>
              {poll.projectConnection.name}
            </Label>
          ) : null,
          poll.deletedAt ? <Label theme="red">{t("component.community-post.label.removed")}</Label> : null,
        ].filter(isDefined)}
        ref={refContainer}
      >
        <div className="flex flex-col gap-4">
          {Boolean(poll.deletedAt) && !sessionUser.isSuperAdmin && (
            <p className="whitespace-pre-line text-grey-700">{t("component.community-post.content.removed")}</p>
          )}
          {(!poll.deletedAt || sessionUser.isAdmin) && (
            <>
              {/* Content */}
              <p
                className={twJoin("whitespace-pre-line text-body-bold", poll.deletedAt && "text-grey-700 line-through")}
              >
                {poll.title}
              </p>
              <p className={poll.deletedAt ? "text-grey-700 line-through" : "text-grey-900"}>
                <LinkFormatter>{poll.description}</LinkFormatter>
              </p>

              {/* Attachment */}
              {poll.images.length > 0 && (
                <div className="w-full lg:w-2/3">
                  <CommunityItemGallery images={poll.images} shouldLoad={hasBeenInViewport} />
                </div>
              )}
              {poll.videos.length > 0 && (
                <div className="w-full lg:w-2/3">
                  <CommunityItemVideo video={poll.videos[0]} shouldLoad={hasBeenInViewport} />
                </div>
              )}
              {poll.documents && poll.documents.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {poll.documents.map((x) => (
                    <Pdf
                      key={x.id}
                      previewImage={x.previewImage}
                      fileName={x.fileName}
                      fileSize={x.fileSize}
                      onClick={() => window.open(x.url, "_blank")}
                    />
                  ))}
                </div>
              )}

              {/* Poll */}
              <div className="flex w-full flex-col gap-4 rounded-lg border border-grey-100 p-4 lg:w-2/3">
                {endDate && <CountdownLabel endDate={endDate} />}

                <span className={twResolve("text-headline3", poll.deletedAt && "text-grey-700 line-through")}>
                  {poll.question}
                </span>

                {poll.canRespond === "yes" ? (
                  <CommunityPollVoteForm poll={poll} />
                ) : (
                  <CommunityPollResultSection poll={poll} />
                )}

                {poll.canSeeResults && (
                  <Button className="mx-auto" styling="ghostPrimary" onClick={resultModalHandler.setTrue}>
                    {t("component.community-poll.button.view-results")}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="-my-1 flex flex-row-reverse items-center justify-between gap-4 pb-2">
          {poll.updatedAt && (
            <span className="text-caption text-grey-700">
              {t("component.community-post.last-activity", {
                time: formatDistance(t, {
                  start: new Date(poll.updatedAt),
                }),
              })}
            </span>
          )}
        </div>
      </CommunityItem>

      <CommunityPollResultModal
        isOpened={isResultModalOpened}
        onOpenChange={resultModalHandler.set}
        question={poll.question}
        results={poll.results || []}
      />
      {poll.canDeleteOwn ? (
        <ConfirmModal
          title={t("component.community-poll.delete.modal.title")}
          description={t("component.community-poll.delete.modal.description")}
          isLoading={false}
          theme="danger"
          onReject={deleteModalHandler.setFalse}
          onOpenChange={deleteModalHandler.set}
          rejectBtnProps={{
            "data-testid": "delete-modal-cancel",
          }}
          onResolve={() => {
            void deletePollMutation.mutateAsync({
              pollId: poll.id,
            });
            deleteModalHandler.setFalse();
          }}
          resolveBtnProps={{
            "data-testid": "modal-confirm-delete",
            text: t("common.action.delete"),
          }}
          isOpened={isDeleteModalOpened}
          shouldCloseOnEsc
          data-testid="cancel-edit-modal"
        />
      ) : (
        <CodeOfConductDeletionModal
          isLoading={deletePollMutation.isPending}
          isOpened={isDeleteModalOpened}
          onOpenChange={deleteModalHandler.set}
          onDelete={async () => {
            await deletePollMutation.mutateAsync({
              pollId: poll.id,
            });
            deleteModalHandler.setFalse();
          }}
        />
      )}
      <ConfirmModal
        title={t("component.community-post.app-poll.modal.title")}
        description={t("component.community-post.app-poll.modal.description")}
        renderDescription={(description) => (
          <div className="flex flex-col items-center">
            <span>{description}</span>
            <Anchor to={t("component.community-post.app-poll.modal.link")}>
              {t("component.community-post.app-poll.modal.link")}
            </Anchor>
          </div>
        )}
        isLoading={false}
        theme="info"
        onResolve={appPollModalHandler.setFalse}
        onReject={appPollModalHandler.setFalse}
        onOpenChange={appPollModalHandler.set}
        rejectBtnProps={{
          hide: true,
        }}
        resolveBtnProps={{
          "data-testid": "app-poll-modal-confirm",
          text: t("common.action.ok"),
        }}
        isOpened={isAppPollModalOpened}
        shouldCloseOnEsc
        data-testid="cancel-app-poll-modal"
      />
    </>
  );
}
